import { useContext, useEffect, useState } from 'react';
import { gql, GraphQLClient } from 'graphql-request';
import { QueryClient, useQuery, useQueryClient } from '@tanstack/react-query';

import { clone, Font, Style, USER_ROLE } from '../constants';
import { StyleBuilder } from '../components/article/StyleBuilder';
import { Logger } from '../utils/Logger';
import { AppContext } from '../App.context';
import { QueryRequest } from '@/services/query-client/QueryClient';
import {
	getHTTPHeaders,
	GRAPHQL_URL,
} from '@/views/home/auth/Authentication.hooks';
import { useIsOnline } from '@/hooks';

const logger = new Logger('Assets', {
	log: 'rgb(50, 173, 230)',
	debug: 'rgb(0, 113, 164)',
	info: 'rgb(36, 138, 61)',
	error: 'rgb(255, 45, 85)',
});

export function useAssets(): AssetsResponse {
	const queryKey = ['assets'];
	const isOnline = useIsOnline();
	const queryClient = useQueryClient();
	const isAdmin = localStorage.getItem(USER_ROLE) === 'admin';
	const { parseGraphQLClientError, queryRequest } = useContext(AppContext);

	useEffect(() => {
		if (isOnline) {
			queryClient.invalidateQueries({ queryKey });
			queryClient.removeQueries({ queryKey });
		}
	}, [isOnline]);

	const [fonts, setFonts] = useState<Array<Font>>([]);
	const [styles, setStyles] = useState<Array<Style>>([]);
	const cacheTime = isAdmin ? 0 : 1000 * 60 * 30; // 30 minutes

	const staleTime = cacheTime;

	const { data, isPending, error } = useQuery<AssetsQuery>({
		queryKey,
		queryFn: async () =>
			getAssets({ queryRequest, queryKey, queryClient, isOnline }),
		staleTime,
		gcTime: isOnline ? cacheTime : Infinity,
		retry: isOnline ? 3 : 0,
	});

	useEffect(() => {
		if (isPending) return;
		if (error) {
			console.error(error);
			return;
		}
		if (!data) return;
		const processedStyles = buildStyles(data.styles);
		const processedFonts = data.fonts.map(mapFontUrls);
		logger.debug(
			`Total of styles: ${processedStyles.length}\n`,
			processedStyles,
		);
		logger.debug(
			`Total of fonts: ${processedFonts.length}\n`,
			processedFonts,
		);

		setFonts(processedFonts);
		setStyles(processedStyles);
	}, [isPending, data, error]);
	return {
		styles,
		fonts,
		isLoading: isPending,
		error: error ? parseGraphQLClientError(error) : null,
	};
}

async function getAssets({
	queryRequest,
	queryClient,
	queryKey,
	isOnline,
}: {
	queryRequest: QueryRequest;
	queryClient: QueryClient;
	queryKey: Array<any>;
	isOnline: boolean;
}): Promise<AssetsQuery> {
	const cached: AssetsQuery | undefined = queryClient.getQueryData(queryKey);

	// The app is offline
	if (!isOnline) {
		if (!cached)
			return {
				styles: [],
				fonts: [],
			};
		return cached;
	}

	// The app is online i will create a graphql client and make the request
	const headers = getHTTPHeaders(false);
	const client = new GraphQLClient(GRAPHQL_URL, { headers });

	try {
		return await queryRequest.queryWithClient(client, assetsQueryDocument);
	} catch (e) {
		console.error(e);
		if (cached) return cached;
	}

	return {
		styles: [],
		fonts: [],
	};
}

interface AssetsQuery {
	styles: Style[];
	fonts: Font[];
}

export function useLoadFonts(fonts: Array<Font>) {
	const promises: Array<Promise<void>> = [];
	useEffect(() => {
		if (!fonts.length) return;
		for (const font of fonts) {
			promises.push(loadFont(font));
		}
		Promise.all(promises)
			.then(() => {
				logger.info(`Success loading fonts`);
			})
			.catch(logger.error);
	}, [fonts]);
}

function mapFontUrls(f: Font) {
	const font = clone(f) as Font;
	font.urls = font.urls.map((url) =>
		url.replace(
			/(https:\/\/cdn\.pwa\.canvasflow\.io\/fonts)+/gim,
			'https://fonts.canvasflow.work',
		),
	);
	return font;
}

function buildStyles(styles: Array<Style>): Array<Style> {
	styles = styles.map(clone).map(StyleBuilder.mapRenameProperties);
	styles = StyleBuilder.resolveInheritance(styles);

	return styles;
}

interface AssetsResponse {
	styles: Array<Style>;
	fonts: Array<Font>;
	isLoading: boolean;
	error: Error | null;
}

async function loadFont(font: Font): Promise<void> {
	const { name, urls } = font;
	const promises = [];

	for (const url of urls) {
		if (!/.woff2$/.test(url)) {
			continue;
		}
		// THIS DOWNLOADS THE FONT

		promises.push(
			new Promise<void>((resolve) => {
				const fontFace = new FontFace(name, `url(${url})`);
				fontFace
					.load()
					.then((loadedFace) => {
						document.fonts.add(loadedFace);
						resolve();
					})
					.catch((err) => {
						console.error(err);
						resolve();
					});
			}),
		);
	}
	await Promise.all(promises);
}

export const assetsQueryDocument = gql`
	query GetAssets {
		styles {
			id
			name
			description
			properties
			supportedDevices
			type
			parent
			tablet
			desktop
			created
			lastModified
		}
		fonts {
			name
			urls
		}
	}
`;
